import { phoneValidator } from "modules/common/services/form/values";
import * as yup from "yup";
import { ApplicationSettingsSnapshotType, fields } from "./models/settings";

const { object, number, string, boolean } = yup;

export const settingsLabels = () => ({
  [fields.productionBudget]: "Бюджет для произв сотрудников",
  [fields.productionQuality]: "Качество для произв сотрудников",
  [fields.engineerBudget]: "Бюджет для ГИПа",
  [fields.engineerQuality]: "Качество для ГИПа",
  [fields.engineerTime]: "Сроки для ГИПа",
  [fields.companyName]: "Организация (полное наименование)",
  [fields.shortCompanyName]: "Краткое наименование",
  [fields.address]: "Юр. адрес",
  [fields.phoneNumber]: "Телефон",
  [fields.email]: "E-mail",
  [fields.sendEmail]: "E-mail для отправки писем",
  [fields.emailPassword]: "Пароль для отправки электронных писем",
  [fields.emailServer]: "Адрес stmp сервера для отправки электронных писем",
  [fields.emailPort]: "Порт для отправки электронных писем",
  [fields.emailReadServer]: "Адрес imap сервера для чтения электронных писем",
  [fields.emailReadPort]: "Порт для чтения электронных писем",
  [fields.isSsl]: "Защита соединения SSL",
  [fields.okved]: "ОКВЭД",
  [fields.directorName]: "Гендиректор ФИО",
  [fields.directorNameGenitive]: "Гендиректор ФИО (Родит. падеж)",
  [fields.administratorPositions]: "ID должности сис.админа",
  [fields.automaticNetworks]: "IP-адреса для автоматического входа",
  [fields.showMinutes]: "Отображать минуты в табеле",
  [fields.prevDaysCount]: "Кол-во предыдущих дней для корректировки в табеле",
  [fields.futureDaysCount]: "Кол-во будущих дней для корректировки в табеле",
  [fields.roundStep]: "Шаг округления",
  [fields.appVersion]: "Версия приложения",
  [fields.oneCUrl]: "Адрес OData-сервиса 1С",
  [fields.oneCLogin]: "Логин подключения к 1С",
  [fields.oneCPassword]: "Пароль подключения к 1С",
  [fields.oneCEnabled]: "Интеграция с 1С включена",
  [fields.overheadDistributionEnabled]: "Распределять накладные расходы по проектам",
  [fields.workloadPlanMode]: "Поведение планового табеля",
  [fields.questionsBoardEnabled]: "Системные колонки доски по проектам",
  [fields.questionsCustomColumnsEnabled]: "Свои колонки на доске заданий",
  [fields.questionsPortfoliosEnabled]: "Личные доски заданий",
  [fields.questionsPortfolioAuthors]: "Кому разрешено заводить свои доски",
  [fields.overheadFixedShare]: "Фиксированная часть накладных, % от норматива",
  [fields.overheadTaxShare]: "в т.ч. налоги, %",
  [fields.overheadPaymentShare]: "в т.ч. обслуживание входящих платежей, %",
});

const asNumber = (value: any) => (isNaN(value) ? undefined : value);

/** IPv4-адрес (192.168.0.1) или подсеть (192.168.0.0/24) */
export const isNetworkAddress = (value: string) => {
  const [ip, mask, ...rest] = (value || "").split("/");

  if (rest.length > 0) {
    return false;
  }

  const octets = ip.split(".");
  const correctIp =
    octets.length === 4 &&
    octets.every((octet) => /^\d{1,3}$/.test(octet) && +octet >= 0 && +octet <= 255);

  if (!correctIp) {
    return false;
  }

  return typeof mask === "undefined" || (/^\d{1,2}$/.test(mask) && +mask >= 0 && +mask <= 32);
};

export const SettingsSchema = (isTestDialog: boolean) => {
  const labels = settingsLabels();
  const shape: TStringMap<any> = {};

  shape[fields.productionBudget] = number()
    .label(labels[fields.productionBudget])
    .transform(asNumber)
    .required()
    // eslint-disable-next-line no-template-curly-in-string
    .typeError("${path} должен быть числом")
    .min(0)
    .test("max", "Сумма показатей должна составлять 100%", function (value: number) {
      const { productionQuality } = this.parent as ApplicationSettingsSnapshotType;
      return value + productionQuality === 100;
    });

  shape[fields.showMinutes] = boolean().label(labels[fields.showMinutes]).notRequired();

  shape[fields.prevDaysCount] = number().transform(asNumber).label(labels[fields.prevDaysCount]).notRequired();
  shape[fields.futureDaysCount] = number().transform(asNumber).label(labels[fields.futureDaysCount]).notRequired();
  shape[fields.roundStep] = number().transform(asNumber).label(labels[fields.roundStep]).notRequired().min(0).max(3);

  shape[fields.isSsl] = boolean().label(labels[fields.isSsl]).notRequired();

  shape[fields.productionBudgetDelta] = number()
    .label("Допуск")
    .transform(asNumber)
    .required()
    // eslint-disable-next-line no-template-curly-in-string
    .typeError("${path} должен быть числом")
    .min(0)
    .max(100);

  shape[fields.productionQuality] = number()
    .label(labels[fields.productionQuality])
    .transform(asNumber)
    .required()
    // eslint-disable-next-line no-template-curly-in-string
    .typeError("${path} должен быть числом")
    .min(0)
    .test("max", "Сумма показатей должна составлять 100%", function (value: number) {
      const { productionBudget } = this.parent as ApplicationSettingsSnapshotType;
      return value + productionBudget === 100;
    });

  shape[fields.engineerBudget] = number()
    .label(labels[fields.engineerBudget])
    .transform(asNumber)
    .required()
    // eslint-disable-next-line no-template-curly-in-string
    .typeError("${path} должен быть числом")
    .min(0)
    .test("max", "Сумма показатей должна составлять 100%", function (value: number) {
      const { engineerQuality, engineerTime } = this.parent as ApplicationSettingsSnapshotType;
      return value + engineerQuality + engineerTime === 100;
    });

  shape[fields.engineerBudgetDelta] = number()
    .label("Допуск")
    .transform(asNumber)
    .required()
    // eslint-disable-next-line no-template-curly-in-string
    .typeError("${path} должен быть числом")
    .min(0)
    .max(100);

  shape[fields.engineerQuality] = number()
    .label(labels[fields.engineerQuality])
    .transform(asNumber)
    .required()
    // eslint-disable-next-line no-template-curly-in-string
    .typeError("${path} должен быть числом")
    .min(0)
    .test("max", "Сумма показатей должна составлять 100%", function (value: number) {
      const { engineerBudget, engineerTime } = this.parent as ApplicationSettingsSnapshotType;
      return value + engineerBudget + engineerTime === 100;
    });

  shape[fields.engineerQualityDelta] = number()
    .label("Допуск")
    .transform(asNumber)
    .required()
    // eslint-disable-next-line no-template-curly-in-string
    .typeError("${path} должен быть числом")
    .min(0)
    .max(100);

  shape[fields.engineerTime] = number()
    .label(labels[fields.engineerTime])
    .transform(asNumber)
    .required()
    // eslint-disable-next-line no-template-curly-in-string
    .typeError("${path} должен быть числом")
    .min(0)
    .test("max", "Сумма показатей должна составлять 100%", function (value: number) {
      const { engineerBudget, engineerQuality } = this.parent as ApplicationSettingsSnapshotType;
      return value + engineerBudget + engineerQuality === 100;
    });

  shape[fields.engineerTimeDelta] = number()
    .label("Допуск")
    .transform(asNumber)
    .required()
    // eslint-disable-next-line no-template-curly-in-string
    .typeError("${path} должен быть числом")
    .min(0);

  shape[fields.companyName] = string().label(labels[fields.companyName]).required();
  shape[fields.shortCompanyName] = string().label(labels[fields.shortCompanyName]).required();
  shape[fields.address] = string().label(labels[fields.address]).required();
  shape[fields.phoneNumber] = phoneValidator(
    labels[fields.phoneNumber],
    string()
      .test("needPhone", "Необходимо указать телефон", function (value) {
        const agent: ApplicationSettingsSnapshotType = this.parent;

        return !!value || !!agent.email;
      })
      .required()
  );

  shape[fields.email] = string()
    .label(labels[fields.email])
    .test("needEmail", "Необходимо указать email", function (value) {
      const agent: ApplicationSettingsSnapshotType = this.parent;

      return !!value || !!agent.phoneNumber;
    })
    .email();

  shape[fields.sendEmail] = string()
    .label(labels[fields.sendEmail])
    .test("needEmail", "Необходимо указать email", function (value) {
      const agent: ApplicationSettingsSnapshotType = this.parent;

      return !!value || !!agent.phoneNumber;
    })
    .email();
  shape[fields.emailPassword] = string().label(labels[fields.emailPassword]).notRequired();
  shape[fields.emailServer] = string().label(labels[fields.emailServer]).notRequired();
  shape[fields.emailPort] = number()
    .label(labels[fields.emailPort])
    .transform(asNumber)
    .notRequired()
    // eslint-disable-next-line no-template-curly-in-string
    .typeError("${path} должен быть числом")
    .min(0);
  shape[fields.emailReadServer] = string().label(labels[fields.emailReadServer]).notRequired();
  shape[fields.emailReadPort] = number()
    .label(labels[fields.emailReadPort])
    .transform(asNumber)
    .notRequired()
    // eslint-disable-next-line no-template-curly-in-string
    .typeError("${path} должен быть числом")
    .min(0);
  shape[fields.okved] = string().label(labels[fields.okved]).required();
  shape[fields.directorName] = string().label(labels[fields.directorName]).required();
  shape[fields.directorNameGenitive] = string().label(labels[fields.directorNameGenitive]).required();

  shape[fields.administratorPositions] = string().label(labels[fields.administratorPositions]).notRequired();

  shape[fields.automaticNetworks] = yup
    .array()
    .label(labels[fields.automaticNetworks])
    .notRequired()
    .test(
      "network",
      "Укажите корректный IP-адрес (192.168.0.1) или подсеть (192.168.0.0/24)",
      (value: any[]) => !value || value.every((network) => isNetworkAddress(network.address))
    );

  shape[fields.appVersion] = string().label(labels[fields.appVersion]).notRequired();

  shape[fields.oneCUrl] = string().label(labels[fields.oneCUrl]).notRequired();
  shape[fields.oneCLogin] = string().label(labels[fields.oneCLogin]).notRequired();
  shape[fields.oneCPassword] = string().label(labels[fields.oneCPassword]).notRequired();
  shape[fields.oneCEnabled] = boolean().label(labels[fields.oneCEnabled]).notRequired();

  shape[fields.overheadDistributionEnabled] = boolean()
    .label(labels[fields.overheadDistributionEnabled])
    .notRequired();

  shape[fields.workloadPlanMode] = string().label(labels[fields.workloadPlanMode]).notRequired();

  shape[fields.questionsBoardEnabled] = boolean()
    .label(labels[fields.questionsBoardEnabled])
    .notRequired();

  shape[fields.questionsCustomColumnsEnabled] = boolean()
    .label(labels[fields.questionsCustomColumnsEnabled])
    .notRequired();

  shape[fields.questionsPortfoliosEnabled] = boolean()
    .label(labels[fields.questionsPortfoliosEnabled])
    .notRequired();

  shape[fields.questionsPortfolioAuthors] = yup
    .array()
    .of(string())
    .label(labels[fields.questionsPortfolioAuthors])
    .notRequired();

  shape[fields.overheadFixedShare] = number()
    .label(labels[fields.overheadFixedShare])
    .transform(asNumber)
    .notRequired()
    // eslint-disable-next-line no-template-curly-in-string
    .typeError("${path} должен быть числом")
    .min(0)
    .max(100);

  shape[fields.overheadTaxShare] = number()
    .label(labels[fields.overheadTaxShare])
    .transform(asNumber)
    .notRequired()
    // eslint-disable-next-line no-template-curly-in-string
    .typeError("${path} должен быть числом")
    .min(0)
    .max(100);

  shape[fields.overheadPaymentShare] = number()
    .label(labels[fields.overheadPaymentShare])
    .transform(asNumber)
    .notRequired()
    // eslint-disable-next-line no-template-curly-in-string
    .typeError("${path} должен быть числом")
    .min(0)
    .max(100);

  return object().shape(shape);
};
